import type { Puzzle } from './scoring'
import { formatDate } from './puzzles'
import { MIN_PLAYERS, topPercent, type CommunityStats } from './stats'

export interface Placing {
  /** 1 for the top play; equal scores share a place. */
  rank: number
  /** How many legal plays the puzzle has. */
  total: number
}

/** Where a score places among every legal play, or null for puzzles without the tally. */
export function placing(puzzle: Puzzle, score: number): Placing | null {
  const tally = puzzle.play_scores
  if (!tally?.length) return null
  const total = tally.reduce((n, s) => n + s.count, 0)
  const rank = 1 + tally.filter((s) => s.score > score).reduce((n, s) => n + s.count, 0)
  return { rank, total }
}

/** Percentage of the top score, never rounded up to 100 unless it is the top score. */
export function percentOfTop(score: number, top: number): number {
  if (top <= 0) return 0
  return score >= top ? 100 : Math.min(99, Math.round((score / top) * 100))
}

/**
 * The text players share. It gives the percentage of the top score, the
 * play's rank and, once the day has enough players, how it compares with
 * theirs; never the scores themselves, so it doesn't spoil the puzzle.
 */
export function shareText(puzzle: Puzzle, score: number, stats?: CommunityStats | null): string {
  const top = puzzle.best_play?.score ?? 0
  const pct = percentOfTop(score, top)
  const filled = Math.floor(pct / 10)
  const bar = '🟩'.repeat(filled) + '⬜'.repeat(10 - filled)
  const place = placing(puzzle, score)
  const lines = [
    `wordevery.day · ${formatDate(puzzle.date, { weekday: 'short', day: 'numeric', month: 'short' })}`,
    `${bar} ${pct}%`,
  ]
  if (place) lines.push(`#${place.rank.toLocaleString('en')} of ${place.total.toLocaleString('en')} plays`)
  if (stats && stats.submissions >= MIN_PLAYERS) {
    const players = `${stats.submissions.toLocaleString('en')} players`
    lines.push(score >= stats.high_score ? `Top score of ${players}` : `Top ${topPercent(stats, score)}% of ${players}`)
  }
  lines.push('https://wordevery.day')
  return lines.join('\n')
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed'

/**
 * Shares the results text: the share sheet on phones and tablets, the
 * clipboard everywhere else. Desktop browsers on Windows (Chrome, Edge) also
 * have navigator.share, but it opens the Windows share dialog, which often has
 * nowhere useful to send text or fails outright; copying is what people expect
 * there. If the share sheet fails for any reason but the player closing it,
 * the text is copied instead.
 */
export async function shareResults(text: string, env: { touch: boolean } = { touch: hasTouchScreen() }): Promise<ShareOutcome> {
  if (env.touch && typeof navigator.share === 'function' && (navigator.canShare?.({ text }) ?? true)) {
    try {
      await navigator.share({ text })
      return 'shared'
    } catch (err) {
      if ((err as DOMException | undefined)?.name === 'AbortError') return 'cancelled'
      // Not allowed, no share targets, …: fall through to copying.
    }
  }
  return (await copyText(text)) ? 'copied' : 'failed'
}

/** A coarse pointer (finger) is the best sign of a device with a useful share sheet. */
function hasTouchScreen(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
}

/** Copies text with the Clipboard API, or the older execCommand where that is blocked or missing. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Missing, or refused (permissions, an embedded frame): try the old way.
  }
  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.append(area)
  area.select()
  try {
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    area.remove()
  }
}
